'use client'

import { usePathname } from 'next/navigation'
import {
  createContext,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  useState,
} from 'react'

const noop = () => undefined

export type NavigationContextType = {
  /**
   * Whether there is a valid login state.
   */
  loggedIn: boolean

  /**
   * Update login state.
   */
  setLoggedIn: Dispatch<SetStateAction<boolean>>

  /**
   * Current global navigation value.
   */
  value: string

  /**
   * setter for the current global navigation value.
   */
  setValue: Dispatch<SetStateAction<string>>
}

export interface NavigationProviderOptions {
  /**
   * Current page path.
   */
  currentPath?: string

  /**
   * Disable the article scroll bar inside the header.
   *
   * This flag is intended to be used in the header widget.
   */
  disableProgressScrollbar?: boolean

  /**
   * Disable tracking of outside interaction.
   *
   * Due to shadow DOM limitations, outside interactions leads to incorrect navigation menu behaviour.
   * This flag is intended to be used in the header widget.
   */
  disableOutsideInteraction?: boolean
}

const defaultContext: NavigationContextType = {
  loggedIn: false,
  setLoggedIn: noop,
  value: '',
  setValue: noop,
}

export const NavigationContext = createContext<
  NavigationContextType & NavigationProviderOptions
>(defaultContext)

export interface NavigationProviderProps {
  options?: NavigationProviderOptions
  initialContext?: Partial<NavigationContextType>
}

export const NavigationProvider = ({
  children,
  options,
  initialContext = defaultContext,
}: PropsWithChildren<NavigationProviderProps>) => {
  const [loggedIn, setLoggedIn] = useState<boolean>(
    initialContext.loggedIn ?? false,
  )
  const [value, setValue] = useState<string>(initialContext.value ?? '')
  const currentPath = usePathname()

  return (
    <NavigationContext.Provider
      value={{
        ...initialContext,
        value,
        setValue,
        currentPath: options?.currentPath ?? currentPath,
        loggedIn,
        setLoggedIn,
        ...options,
      }}
    >
      {children}
    </NavigationContext.Provider>
  )
}
